import React from "react";
import { Table, Tag, Pagination, Spin, Popover, Button, Modal, Form, Input, InputNumber, Switch, message, Select } from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import useIsMobile from "../../hooks/useIsMobile";
import orderService from "../../services/OrderLists";

const { TextArea } = Input;

const SolutionMetaEditModal = ({ visible, onCancel, record, onRefresh }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (visible && record) {
            form.setFieldsValue({
                expert_rating: record.expert_rating === "N/A" ? null : record.expert_rating,
                quality_check_completed: record.quality_check_completed || false,
                remark: record.remark || "",
                task_type: record.task_type || undefined,
                ai_report_added: record.ai_report_added || false,
                plag_report_added: record.plag_report_added || false,
                english_country: record.english_country || undefined,
                english_country_other: record.english_country_other || "",
                referencing_format: record.referencing_format || undefined,
                referencing_format_other: record.referencing_format_other || "",
            });
        }
    }, [visible, record, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const response = await orderService.patchSolutionMeta(record.id, values);

            if (response.success) {
                message.success("Solution Meta updated successfully");
                onRefresh();
                onCancel();
            } else {
                message.error("Failed to update Solution Meta");
            }
        } catch (error) {
            console.error("Update error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={`Edit Solution Meta - ${record?.task_key}`}
            visible={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="expert_rating"
                    label="Expert Rating"
                    rules={[{ type: 'number', min: 0, max: 10, message: 'Rating must be between 0 and 10' }]}
                >
                    <InputNumber style={{ width: '100%' }} placeholder="Enter rating (0-10)" />
                </Form.Item>
                <Form.Item
                    name="task_type"
                    label="Task Type"
                    rules={[{ required: true, message: "Please select Task Type" }]}
                >
                    <Select placeholder="Select Task Type">
                        <Select.Option value="regular">Regular</Select.Option>
                        <Select.Option value="feedback">Feedback</Select.Option>
                        <Select.Option value="milestone">Milestone</Select.Option>
                        <Select.Option value="amendment">Amendment</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    name="ai_report_added"
                    label="AI Report Added"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="plag_report_added"
                    label="Plag Report Added"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="quality_check_completed"
                    label="Quality Check Completed"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="english_country"
                    label="English Country"
                >
                    <Select placeholder="Select English country">
                        <Select.Option value="us">US</Select.Option>
                        <Select.Option value="uk">UK</Select.Option>
                        <Select.Option value="australia">Australia</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.english_country !== currentValues.english_country}
                >
                    {({ getFieldValue }) =>
                        getFieldValue("english_country") === "other" ? (
                            <Form.Item
                                name="english_country_other"
                                label="English Country (Other)"
                                rules={[{ required: true, message: "Please specify country" }]}
                            >
                                <Input placeholder="Specify country" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>

                <Form.Item
                    name="referencing_format"
                    label="Referencing Format"
                >
                    <Select placeholder="Select referencing format">
                        <Select.Option value="harvard">Harvard</Select.Option>
                        <Select.Option value="apa7">APA 7th</Select.Option>
                        <Select.Option value="apa6">APA 6th</Select.Option>
                        <Select.Option value="ieee">IEEE</Select.Option>
                        <Select.Option value="oscola">OSCOLA</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.referencing_format !== currentValues.referencing_format}
                >
                    {({ getFieldValue }) =>
                        getFieldValue("referencing_format") === "other" ? (
                            <Form.Item
                                name="referencing_format_other"
                                label="Referencing Format (Other)"
                                rules={[{ required: true, message: "Please specify referencing format" }]}
                            >
                                <Input placeholder="Specify referencing format" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>

                <Form.Item name="remark" label="Remark">
                    <TextArea rows={4} placeholder="Enter remark" />
                </Form.Item>
            </Form>
        </Modal>
    );
};



const renderRow = (key, value) => (
    <div
        style={{
            display: "flex",
            borderBottom: "1px solid #e8e8e8",
            alignItems: "flex-start",
            minHeight: "44px",
            justifyContent: "flex-start",
        }}>
        <div
            style={{
                width: "35%",
                padding: "8px",
                fontWeight: "bold",
                borderRight: "1px solid #e8e8e8",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
            }}>
            <strong>{key}: </strong>
        </div>
        <div
            style={{
                width: "65%",
                padding: "8px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
                wordBreak: "break-word"
            }}>
            <span>{value}</span>
        </div>
    </div>
);

const MobileSolutionMetaTable = ({
    data,
    loading,
    pagination,
    setPagination,
    onEdit,
}) => {
    return (
        <Spin spinning={loading}>
            <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "10px", background: '#f2f6f9' }}>
                {data.map((record) => (
                    <div
                        key={record.id || record.task_key}
                        style={{
                            background: "white",
                            borderRadius: "8px",
                            overflow: "hidden",
                            marginTop: '10px'
                        }}>
                        {renderRow("Order Key", (
                            <Link
                                to={`/order-management/my-orders/${record.task_key}`}
                                style={{ fontWeight: "bold" }}
                            >
                                {record.task_key}
                            </Link>
                        ))}
                        {renderRow("Task Title", record.task_title || "N/A")}
                        {renderRow("Task Type", record.task_type || "N/A")}
                        {renderRow("Expert Rating", record.expert_rating || "N/A")}
                        {renderRow("Word Count Achieved", record.word_count_achieved || "N/A")}
                        {renderRow("AI Report", record.ai_report_added ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>)}
                        {renderRow("Plag Report", record.plag_report_added ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>)}
                        {/* {renderRow("Files", (
                            record.files && record.files.length > 0 ? (
                                <Popover
                                    content={
                                        <div style={{ maxWidth: '600px', overflowX: 'auto' }}>
                                            <Table
                                                size="small"
                                                pagination={false}
                                                dataSource={record.files}
                                                rowKey="file_uid"
                                                columns={[
                                                    {
                                                        title: 'Name',
                                                        dataIndex: 'file_name',
                                                        key: 'file_name',
                                                        render: (text, file) => (
                                                            <a href={file.file_url || file.file} target="_blank" rel="noopener noreferrer">
                                                                {text || 'View File'}
                                                            </a>
                                                        )
                                                    },
                                                    { title: 'Type', dataIndex: 'file_type', key: 'file_type' },
                                                    {
                                                        title: 'Finalised',
                                                        dataIndex: 'is_finalised',
                                                        key: 'is_finalised',
                                                        render: (val) => val ? 'Yes' : 'No'
                                                    },
                                                    {
                                                        title: 'Created',
                                                        dataIndex: 'created',
                                                        key: 'created',
                                                        render: (date) => date ? moment(date).format("DD-MM-YYYY") : "N/A"
                                                    },
                                                    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
                                                ]}
                                            />
                                        </div>
                                    }
                                    title="File Details"
                                    trigger="click"
                                >
                                    <Button type="link" style={{ padding: 0 }}>View</Button>
                                </Popover>
                            ) : "No Files"
                        ))} */}
                        {renderRow("English Country", record.english_country || record.english_country_other || "N/A")}
                        {renderRow("Referencing Format", record.referencing_format || record.referencing_format_other || "N/A")}
                        {renderRow("Created On", record.created_at ? moment(record.created_at).format("DD-MM-YYYY hh:mm A") : "N/A")}
                        <div style={{ padding: "8px", display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => onEdit(record)}
                                size="small"
                                style={{ background: "#040724" }}
                            >
                                Edit
                            </Button>
                        </div>
                    </div>
                ))}
                <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(page, pageSize) =>
                        setPagination((prev) => ({ ...prev, current: page, pageSize }))
                    }
                    showSizeChanger
                    style={{ textAlign: "center", marginTop: 16 }}
                />
            </div>
        </Spin>
    );
};

const SolutionMetaTable = ({
    data,
    loading,
    pagination,
    setPagination,
    onRefresh,
}) => {
    const isMobile = useIsMobile();
    const [editModalVisible, setEditModalVisible] = React.useState(false);
    const [selectedRecord, setSelectedRecord] = React.useState(null);

    const handleEdit = (record) => {
        setSelectedRecord(record);
        setEditModalVisible(true);
    };

    const columns = [
        {
            title: "Order Key",
            dataIndex: "task_key",
            key: "task_key",
            render: (text) => (
                <Link
                    to={`/order-management/my-orders/${text}`}
                    style={{ fontWeight: "bold" }}
                >
                    {text}
                </Link>
            ),
        },
        {
            title: "Task Title",
            dataIndex: "task_title",
            key: "task_title",
            render: (text) => (
                <Popover content={text || "N/A"}>
                    <div style={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer"
                    }}>
                        {text || "N/A"}
                    </div>
                </Popover>
            ),
        },
        {
            title: "Task Type",
            dataIndex: "task_type",
            key: "task_type",
        },
        {
            title: "Author",
            dataIndex: "author",
            key: "author",
        },
        {
            title: "Expert Rating",
            dataIndex: "expert_rating",
            key: "expert_rating",
            render: (text) => text || "N/A",
        },
        {
            title: "Word Count Achieved",
            dataIndex: "word_count_achieved",
            key: "word_count_achieved",
            render: (text) => text || "N/A",
        },
        {
            title: "AI Report",
            dataIndex: "ai_report_added",
            key: "ai_report_added",
            render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
        },
        {
            title: "Plag Report",
            dataIndex: "plag_report_added",
            key: "plag_report_added",
            render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
        },
        // {
        //     title: "Files",
        //     dataIndex: "files",
        //     key: "files",
        //     render: (files) => (
        //         files && files.length > 0 ? (
        //             <Popover
        //                 content={
        //                     <div style={{ maxWidth: '600px', overflowX: 'auto' }}>
        //                         <Table
        //                             size="small"
        //                             pagination={false}
        //                             dataSource={files}
        //                             rowKey="file_uid"
        //                             columns={[
        //                                 {
        //                                     title: 'Name',
        //                                     dataIndex: 'file_name',
        //                                     key: 'file_name',
        //                                     render: (text, file) => (
        //                                         <a href={file.file_url || file.file} target="_blank" rel="noopener noreferrer">
        //                                             {text || 'View File'}
        //                                         </a>
        //                                     )
        //                                 },
        //                                 { title: 'Type', dataIndex: 'file_type', key: 'file_type' },
        //                                 {
        //                                     title: 'Finalised',
        //                                     dataIndex: 'is_finalised',
        //                                     key: 'is_finalised',
        //                                     render: (val) => val ? 'Yes' : 'No'
        //                                 },
        //                                 {
        //                                     title: 'Created',
        //                                     dataIndex: 'created',
        //                                     key: 'created',
        //                                     render: (date) => date ? moment(date).format("DD-MM-YYYY") : "N/A"
        //                                 },
        //                                 { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
        //                             ]}
        //                         />
        //                     </div>
        //                 }
        //                 title="File Details"
        //                 trigger="click"
        //             >
        //                 <Button type="link" style={{ padding: 0 }}>View</Button>
        //             </Popover>
        //         ) : "No Files"
        //     ),
        // },
        {
            title: "Created On",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => (date ? moment(date).format("DD-MM-YYYY hh:mm A") : "N/A"),
            sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
        },

        {
            title: "English Country",
            key: "location",
            render: (_, record) => record.english_country || record.english_country_other || "N/A",
        },
        {
            title: "Referencing Format",
            key: "referencing_format",
            render: (_, record) => record.referencing_format || record.referencing_format_other || "N/A",
        },
        {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 100,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                />
            ),
        },
    ];

    return (
        <div style={{ width: "100%" }}>
            {isMobile ? (
                <MobileSolutionMetaTable
                    data={data}
                    loading={loading}
                    pagination={pagination}
                    setPagination={setPagination}
                    onEdit={handleEdit}
                />
            ) : (
                <Table
                    style={{ width: "100%" }}
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey={(record) => record.id || record.key}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, pageSize) => {
                            setPagination({ ...pagination, current: page, pageSize });
                        },
                        onShowSizeChange: (current, size) => {
                            setPagination({ ...pagination, current: 1, pageSize: size });
                        },
                        showSizeChanger: true,
                    }}
                />
            )}
            <SolutionMetaEditModal
                visible={editModalVisible}
                record={selectedRecord}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedRecord(null);
                }}
                onRefresh={onRefresh}
            />
        </div>
    );
};

export default SolutionMetaTable;
